import CalendarIcon from "@mui/icons-material/CalendarToday";
import Delete from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import {
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import {
  OmnisearchCommandButtonsContext,
  OmnisearchDataDisplay,
} from "components/OmnisearchDataDisplay";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { getFullName } from "encamp-shared/src/utils/name";
import { EventMutationScope, Frequency, TaskStatus, TasksQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useCallback, useEffect, useState } from "react";
import { prettyPrintDateMed } from "util/dates";

import { EditTaskDialog } from "./EditTaskDialog";
import useDeleteTaskMutation from "./hooks/useDeleteTask";
import RecurrenceScopeRadioDialog from "./RecurrenceScopeRadioDialog";
import { TASKS_COUNT_QUERY, TASKS_QUERY } from "./schema";
import { taskStatusToChipSx } from "./statusStyles";
import { TasksCalendar } from "./TasksCalendar";
import { buildBeforeAfterSearchString } from "./util.buildSearchString";
import { taskStatusToLabel } from "./util.labels";

export type Row = TasksQuery["virtualEvents"]["items"][0];

type ViewMode = "list" | "calendar";

function sortTagsAlphabetically(tags: Array<{ id: string; name: string }>) {
  return [...tags].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

const defaultDeleteTaskState = {
  open: false,
  recurring: false,
  virtualEvent: null,
};

export type TasksDisplayProps = {
  additionalFilterColumns: FilterKey[];
  commandButtons: (context: OmnisearchCommandButtonsContext) => React.ReactNode[];
  defaultSearch: string;
  /** If not provided, the default search will be used for the calendar view. */
  defaultCalendarSearch?: string;
  persistenceKey?: string;
};

export const TasksDisplay = ({
  additionalFilterColumns,
  commandButtons,
  defaultSearch,
  defaultCalendarSearch,
  persistenceKey,
}: TasksDisplayProps) => {
  const alerts = useAlerts();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { hasTaskWritePermissionForFacilities } = useAuthorization();

  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const [calendarRange, setCalendarRange] = useState<string>(() =>
    buildBeforeAfterSearchString(DateTime.local())
  );

  useEffect(() => {
    setCalendarRange(buildBeforeAfterSearchString(DateTime.local()));
  }, []);

  const onChangeViewMode = useCallback(
    (event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode) => {
      setViewMode(newViewMode);
    },
    []
  );

  const [deleteTaskState, setDeleteTaskState] = useState<{
    open: boolean;
    recurring: boolean;
    virtualEvent: Pick<Row, "taskId" | "deadline" | "title"> | null;
  }>(defaultDeleteTaskState);
  const resetDeleteTaskState = () => setDeleteTaskState(defaultDeleteTaskState);

  const [deleteTask, { loading: deleteTaskLoading }] = useDeleteTaskMutation();

  const {
    selectedId: virtualEventId,
    pushIdToUrl: pushVirtualEventIdToUrl,
    popIdFromUrl: removeVirtualEventIdFromUrl,
  } = useSelectedIdInUrl("virtualEventId");

  const onRowEdit = (row: Row) => {
    if (!hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])) {
      return; // no op because the user doesn't have permissions
    }

    pushVirtualEventIdToUrl(row.id);
  };

  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      if (deleteTaskState.virtualEvent) {
        deleteTask({
          variables: {
            input: {
              taskId: deleteTaskState.virtualEvent.taskId,
              date: deleteTaskState.virtualEvent.deadline,
              eventMutationScope: scope,
            },
          },
          onCompleted: () => {
            resetDeleteTaskState();
            alerts.success(`Event deleted`);
          },
          onError: (error) => {
            alerts.error(`Error deleting event: ${error.message}`);
          },
          refetchQueries: [TASKS_QUERY, TASKS_COUNT_QUERY],
        });
      }
    },
    [deleteTask, deleteTaskState.virtualEvent, alerts]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Deadline",
      sortable: true,
      flex: 0.7,
      renderCell: ({ value }) => {
        return <CellText variant="body2">{prettyPrintDateMed(value)}</CellText>;
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return <CellText variant="body2">{title}</CellText>;
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: 1.5,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "taskStatus",
      headerName: "Status",
      flex: 0.7,
      sortable: true,
      renderCell({ row: { taskStatus } }) {
        const stylingBlock = taskStatusToChipSx(taskStatus, theme);
        return <Chip label={taskStatusToLabel(taskStatus)} sx={stylingBlock} size="small" />;
      },
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(TaskStatus),
      enumPresentationFunction: (enumValue) => taskStatusToLabel(enumValue),
    },

    {
      field: "assignee",
      headerName: "Assignee",
      sortable: true,
      flex: 1,
      valueGetter(_, row) {
        return getFullName(row.assignee?.person) || row.assignee?.email;
      },
      renderCell: ({ value }) => {
        return <CellText variant="body2">{value}</CellText>;
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 0.7,
      filterKeyType: "taskTags",
      renderCell: ({ row }) => (
        <TagsDisplay
          tags={sortTagsAlphabetically(
            row.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? []
          )}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      width: 30,
      getActions: ({ row }) => {
        const actions = [
          <Tooltip title="Delete Task" key="delete">
            <GridActionsCellItem
              onClick={() =>
                setDeleteTaskState({
                  open: true,
                  recurring: row.frequency !== Frequency.Once,
                  virtualEvent: row,
                })
              }
              label="Delete Task"
              icon={<Delete />}
            />
          </Tooltip>,
        ];

        return hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])
          ? actions
          : [];
      },
    },
  ];

  const viewModeToggle = [
    <ToggleButtonGroup
      key="view-mode-toggle-button-group"
      value={viewMode}
      exclusive
      color="primary"
      size="large"
      onChange={onChangeViewMode}
    >
      Omnisearch
      <ToggleButton value="list">
        <ListIcon />
      </ToggleButton>
      <ToggleButton value="calendar">
        <CalendarIcon />
      </ToggleButton>
    </ToggleButtonGroup>,
  ];

  return (
    <>
      {viewMode === "list" ? (
        <OmnisearchDataGrid
          persistenceKey={persistenceKey ? `${persistenceKey}-table` : "tasks-table"}
          noDataMessage="No tasks have been added to this organization yet."
          isURLDriven
          columns={columns}
          initialSortModel={[{ field: "deadline", sort: "asc" }]}
          dataQuery={TASKS_QUERY}
          defaultSearch={defaultSearch}
          getItems={(data) => data.virtualEvents.items}
          getCount={(data) => data.virtualEvents.count ?? 0}
          excludeFilterColumns={["deadline", "facilities"]}
          additionalFilterColumns={additionalFilterColumns}
          isRowSelectable={() => false}
          onRowClick={({ row }) => onRowEdit(row)}
          startCommandButtons={viewModeToggle}
          commandButtons={commandButtons}
          initialPageSize={10}
          rowHeight={"auto"}
          columnVisibilityModel={{
            ...(isMobile && {
              facilities: false,
              tags: false,
            }),
          }}
        />
      ) : (
        <OmnisearchProvider>
          <OmnisearchDataDisplay
            persistenceKey={persistenceKey ? `${persistenceKey}-calendar` : "tasks-calendar"}
            isURLDriven
            columns={columns}
            dataQuery={TASKS_QUERY}
            dataQueryVariables={{ pageSize: undefined, page: undefined }}
            defaultSearch={`${defaultCalendarSearch ?? defaultSearch} ${calendarRange}`}
            getItems={(data) => data.virtualEvents.items}
            getCount={(data) => data.virtualEvents.count ?? 0}
            excludeFilterColumns={["deadline", "facilities"]}
            additionalFilterColumns={additionalFilterColumns}
            startCommandButtons={viewModeToggle}
            commandButtons={commandButtons}
            dataDisplay={({ items, loading }) => (
              <TasksCalendar
                loading={loading}
                tasks={items}
                onTaskClick={onRowEdit}
                onRangeChange={setCalendarRange}
              />
            )}
          />
        </OmnisearchProvider>
      )}

      {virtualEventId && (
        <EditTaskDialog virtualEventId={virtualEventId} onClose={removeVirtualEventIdFromUrl} />
      )}
      {deleteTaskState.recurring && (
        <RecurrenceScopeRadioDialog
          action="Delete"
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onSubmit={onDeleteTask}
          loading={deleteTaskLoading}
        />
      )}
      {deleteTaskState.open && !deleteTaskState.recurring && (
        <ConfirmDialog
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onConfirm={() => onDeleteTask(EventMutationScope.This)}
          header="Are you sure you want to delete this task?"
          resources={[
            {
              resourceType: "Task",
              resourceName: deleteTaskState.virtualEvent?.title,
            },
          ]}
          loading={deleteTaskLoading}
        />
      )}
    </>
  );
};
